import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { SortableDataTable } from '../components/SortableDataTable';
import { StatusBadge } from '../components/DataTable';
import { ItemFormDialog } from '../components/ItemFormDialog';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { getClasificacionesTela, createClasificacionTela, updateClasificacionTela, deleteClasificacionTela, reorderClasificacionesTela } from '../lib/api';

const formFields = [
    { key: 'nombre', label: 'Nombre', type: 'text', required: true, placeholder: 'Ej: DENIM, COMFORT, JOGG...' },
    { key: 'activo', label: 'Estado', type: 'switch', defaultValue: true },
];

const tableColumns = [
    { key: 'nombre', label: 'Nombre' },
    { key: 'activo', label: 'Estado', render: (val) => <StatusBadge activo={val} /> },
];

export default function ClasificacionesTelaPage() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterActive, setFilterActive] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getClasificacionesTela();
            let filtered = response.data;
            if (search) filtered = filtered.filter(c => c.nombre.toLowerCase().includes(search.toLowerCase()));
            if (filterActive !== null) filtered = filtered.filter(c => c.activo === filterActive);
            setData(filtered);
        } catch (error) {
            toast.error('Error al cargar las clasificaciones');
        } finally {
            setLoading(false);
        }
    }, [search, filterActive]);

    useEffect(() => {
        const debounce = setTimeout(fetchData, 300);
        return () => clearTimeout(debounce);
    }, [fetchData]);

    const handleAdd = () => { setSelectedItem(null); setFormOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setFormOpen(true); };
    const handleDelete = (item) => { setSelectedItem(item); setDeleteOpen(true); };

    const handleReorder = async (newData, reorderItems) => {
        setData(newData);
        try {
            await reorderClasificacionesTela(reorderItems);
        } catch (error) {
            toast.error('Error al reordenar');
            fetchData();
        }
    };

    const handleFormSubmit = async (formData) => {
        setSubmitting(true);
        try {
            if (selectedItem) {
                await updateClasificacionTela(selectedItem.id, formData);
                toast.success('Clasificacion actualizada');
            } else {
                await createClasificacionTela(formData);
                toast.success('Clasificacion creada');
            }
            setFormOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al guardar');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteConfirm = async () => {
        setSubmitting(true);
        try {
            await deleteClasificacionTela(selectedItem.id);
            toast.success('Clasificacion eliminada');
            setDeleteOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al eliminar');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="clasificaciones-tela-page">
            <div>
                <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                    Clasificaciones de Tela
                </h1>
                <p className="text-slate-500 text-sm mt-1">
                    Gestiona las clasificaciones para el catalogo de telas
                </p>
            </div>

            <SortableDataTable
                data={data}
                columns={tableColumns}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onReorder={handleReorder}
                searchValue={search}
                onSearchChange={setSearch}
                filterActive={filterActive}
                onFilterChange={setFilterActive}
                loading={loading}
                emptyMessage="No hay clasificaciones registradas"
                addButtonText="Nueva Clasificacion"
                testIdPrefix="clasificaciones-tela"
                showActionButtons={true}
            />

            <ItemFormDialog
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSubmit={handleFormSubmit}
                item={selectedItem}
                title="Clasificacion de Tela"
                fields={formFields}
                loading={submitting}
                testIdPrefix="clasificacion-tela-form"
            />

            <DeleteConfirmDialog
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDeleteConfirm}
                itemName={selectedItem?.nombre}
                loading={submitting}
                testIdPrefix="clasificacion-tela-delete"
            />
        </div>
    );
}
